import { useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import {
  X, Check, ArrowClockwise, ArrowCounterClockwise, ArrowsOutSimple, ArrowsInSimple,
} from "@phosphor-icons/react";

/**
 * Turn a cropped area (in image pixel coords) into a JPEG Blob at the target
 * pixel dimensions. Runs entirely in the browser via a canvas.
 */
async function cropToBlob(imageSrc, area, rotation = 0, maxW = 2400) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = imageSrc;
  });

  const outW = Math.min(area.width, maxW);
  const outH = Math.round((area.height * outW) / area.width);
  const canvas = document.createElement("canvas");
  canvas.width = outW; canvas.height = outH;
  const ctx = canvas.getContext("2d");

  if (rotation) {
    // Rotate around the crop's centre before drawing.
    ctx.translate(outW / 2, outH / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-outW / 2, -outH / 2);
  }
  ctx.drawImage(
    image, area.x, area.y, area.width, area.height, 0, 0, outW, outH,
  );
  return new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
}

const ASPECT_OPTIONS = [
  { label: "Free", value: null },
  { label: "1:1",  value: 1 },
  { label: "4:5",  value: 4 / 5 },
  { label: "3:2",  value: 3 / 2 },
  { label: "16:9", value: 16 / 9 },
  { label: "9:16", value: 9 / 16 },
];

/**
 * Modal image cropper. Accepts a File OR a src URL, returns a cropped JPEG
 * Blob (max width 2400px, quality 0.92) via `onConfirm(blob, previewUrl)`.
 */
export default function ImageCropperModal({ file, src, initialAspect = null, onCancel, onConfirm }) {
  const [imageSrc] = useState(() =>
    src || (file ? URL.createObjectURL(file) : null)
  );
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [aspect, setAspect] = useState(initialAspect);
  const [busy, setBusy] = useState(false);
  const areaRef = useRef(null);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    areaRef.current = croppedAreaPixels;
  }, []);

  const confirm = async () => {
    if (!areaRef.current) return;
    setBusy(true);
    try {
      const blob = await cropToBlob(imageSrc, areaRef.current, rotation);
      const previewUrl = URL.createObjectURL(blob);
      onConfirm?.(blob, previewUrl);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Crop failed", e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-[#241209]/85 backdrop-blur-sm flex items-center justify-center px-3 py-6"
      data-testid="crop-modal"
      onClick={(e) => e.target === e.currentTarget && onCancel?.()}
    >
      <div className="bg-[#2A1F14] text-[#E4A15C] rounded-2xl w-full max-w-2xl overflow-hidden border border-[#E4A15C]/25 shadow-2xl">
        <div className="flex items-center justify-between px-4 py-2 border-b border-[#E4A15C]/15">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase">Crop & adjust</div>
          <button onClick={onCancel} className="rounded-full p-1.5 hover:bg-[#E4A15C]/10" data-testid="crop-cancel">
            <X size={15} weight="bold" />
          </button>
        </div>

        <div className="relative bg-[#3A2A1C]" style={{ height: "60vh" }}>
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect || undefined}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              restrictPosition
            />
          )}
        </div>

        {/* Controls */}
        <div className="px-4 py-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase opacity-70 mr-1">Aspect</div>
            {ASPECT_OPTIONS.map((o) => (
              <button
                key={o.label}
                onClick={() => setAspect(o.value)}
                data-testid={`crop-aspect-${o.label.toLowerCase()}`}
                className={`rounded-full px-2.5 py-0.5 text-[10px] font-mono-tech tracking-[0.22em] uppercase border transition ${
                  (o.value ?? "free") === (aspect ?? "free")
                    ? "bg-[#E4A15C] text-[#2A1F14] border-[#E4A15C]"
                    : "border-[#E4A15C]/25 text-[#E4A15C] hover:bg-[#E4A15C]/10"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2">
              <ArrowsInSimple size={13} className="opacity-70" />
              <input
                type="range" min="1" max="4" step="0.02" value={zoom}
                onChange={(e) => setZoom(+e.target.value)}
                data-testid="crop-zoom"
                className="flex-1 accent-[#E4A15C]"
              />
              <ArrowsOutSimple size={13} className="opacity-70" />
            </label>
            <div className="flex items-center gap-1 justify-end">
              <button
                onClick={() => setRotation((r) => (r - 90 + 360) % 360)}
                data-testid="crop-rotate-ccw"
                className="rounded-full p-1.5 border border-[#E4A15C]/25 hover:bg-[#E4A15C]/10"
              ><ArrowCounterClockwise size={13} weight="bold" /></button>
              <span className="font-mono-tech text-[10px] tracking-widest w-8 text-center">{rotation}°</span>
              <button
                onClick={() => setRotation((r) => (r + 90) % 360)}
                data-testid="crop-rotate-cw"
                className="rounded-full p-1.5 border border-[#E4A15C]/25 hover:bg-[#E4A15C]/10"
              ><ArrowClockwise size={13} weight="bold" /></button>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={onCancel}
              disabled={busy}
              data-testid="crop-cancel-btn"
              className="rounded-full px-4 py-1.5 border border-[#E4A15C]/25 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[#E4A15C]/10 disabled:opacity-40"
            >Cancel</button>
            <button
              onClick={confirm}
              disabled={busy}
              data-testid="crop-confirm-btn"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 bg-[#E4A15C] text-[#2A1F14] font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90 disabled:opacity-40"
            >
              <Check size={13} weight="bold" /> {busy ? "Cropping…" : "Use this crop"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
